<template>
  <div class="app-container">
    <!-- User Profile Banner - Top Center -->
    <div v-if="user && userProfile.gradeLevel" class="user-profile-top-banner">
      <div class="user-profile-content">
        <p class="user-profile-text">
          📚 Grade {{ userProfile.gradeLevel }} • {{ userProfile.firstName || 'Student' }}
          <span v-if="isPremium" class="premium-badge-inline">👑 Premium</span>
        </p>
      </div>
      <div class="user-menu-top">
        <UserMenu 
          :user="user"
          :userProfile="userProfile"
          :isPremium="isPremium"
          :subscriptionDate="subscriptionDate"
          :nextBillingDate="nextBillingDate"
          @logout="handleLogout"
          @menu-open="handleUserMenuOpen"
          @menu-close="handleUserMenuClose"
        />
      </div>
    </div>
    
    <!-- Chalkboard Header -->
    <header class="chalkboard-header">
      <div class="chalkboard-frame">
        <div class="chalkboard-surface">
          <div class="chalkboard-content">
            <h1 class="chalkboard-title">
              <span class="chalk-text">
                <span class="chalk-letter" style="animation-delay: 0.2s">C</span>
                <span class="chalk-letter" style="animation-delay: 0.4s">H</span>
                <span class="chalk-letter" style="animation-delay: 0.6s">A</span>
                <span class="chalk-letter" style="animation-delay: 0.8s">L</span>
                <span class="chalk-letter" style="animation-delay: 1.0s">K</span>
                <span class="chalk-letter" style="animation-delay: 1.2s">B</span>
                <span class="chalk-letter" style="animation-delay: 1.4s">O</span>
                <span class="chalk-letter" style="animation-delay: 1.6s">A</span>
                <span class="chalk-letter" style="animation-delay: 1.8s">R</span>
                <span class="chalk-letter" style="animation-delay: 2.0s">D</span>
                <span class="chalk-space"></span>
                <span class="chalk-letter" style="animation-delay: 2.2s">A</span>
                <span class="chalk-letter" style="animation-delay: 2.4s">I</span>
              </span>
            </h1>
            <p class="chalkboard-subtitle chalk-subtitle">Your AI-Powered Learning Adventure!</p>
          </div>
        </div>
      </div>
    </header>
    
    <!-- Navigation Menu -->
    <nav v-if="user && !userMenuOpen" class="navigation-menu">
      <!-- Mobile Menu Toggle Button -->
      <button class="mobile-menu-toggle" @click="toggleMobileMenu" aria-label="Toggle navigation menu">
        <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
        <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
        <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
      </button>

      <!-- Navigation Container -->
      <div class="nav-container" :class="{ 'mobile-open': mobileMenuOpen }">
        <router-link 
          :to="userProfile.gradeLevel === 'K' ? '/kid-dashboard' : '/dashboard'" 
          class="nav-item" 
          active-class="active" 
          @click="closeMobileMenu">
          <span class="nav-icon">📊</span>
          <span class="nav-text">Dashboard</span>
        </router-link>
        <!-- Hide Adaptive Quiz for Kindergarten users (they use Kid Quiz instead) -->
        <router-link 
          v-if="userProfile.gradeLevel !== 'K'" 
          to="/quiz" 
          class="nav-item" 
          active-class="active" 
          @click="closeMobileMenu">
          <span class="nav-icon">🧠</span>
          <span class="nav-text">Adaptive Quiz</span>
        </router-link>
        <!-- Hide Achievements for Kindergarten users -->
        <router-link 
          v-if="userProfile.gradeLevel !== 'K'" 
          to="/achievements" 
          class="nav-item" 
          active-class="active" 
          @click="closeMobileMenu">
          <span class="nav-icon">🏆</span>
          <span class="nav-text">Achievements</span>
        </router-link>
        <router-link v-if="isPremium" to="/analytics" class="nav-item" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">📈</span>
          <span class="nav-text">Analytics</span>
        </router-link>
        <router-link v-if="isPremium" to="/groups" class="nav-item" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">👥</span>
          <span class="nav-text">Study Groups</span>
        </router-link>
        <router-link v-if="isPremium" to="/social" class="nav-item" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">💬</span>
          <span class="nav-text">Social Feed</span>
        </router-link>
        <router-link to="/export" class="nav-item" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">📄</span>
          <span class="nav-text">Export</span>
        </router-link>
        <router-link to="/subscription" class="nav-item" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">⭐</span>
          <span class="nav-text">Premium</span>
        </router-link>
        
        <!-- Admin Links -->
        <div v-if="user && isAdmin" class="admin-section">
          <h4 class="admin-title">Admin Tools</h4>
          <router-link to="/admin/questions" class="nav-item admin-item" active-class="active" @click="closeMobileMenu">
            <span class="nav-icon">🗄️</span>
            <span class="nav-text">Question Database</span>
          </router-link>
          <router-link to="/admin/groq-upload" class="nav-item admin-item" active-class="active" @click="closeMobileMenu">
            <span class="nav-icon">📤</span>
            <span class="nav-text">Upload Groq Questions</span>
          </router-link>
        </div>
      </div>

      <!-- Mobile Menu Overlay -->
      <div v-if="mobileMenuOpen" class="mobile-menu-overlay" @click="closeMobileMenu"></div>
    </nav>
    
    <!-- Profile Completion Modal -->
    <div v-if="showProfileCompletion" class="modal-overlay">
      <div class="modal">
        <ProfileCompletion v-if="user" :user="user" @profile-completed="handleProfileCompleted" />
        <div v-else class="modal-loading">
          <p>Loading user data...</p>
        </div>
      </div>
    </div>
    
    <!-- Question Source Splash Page -->
    <QuestionSourceSplash 
      v-if="showQuestionSourceSplash" 
      :user-state="userProfile.state"
      :user-profile="userProfile"
      @close="handleSplashClose"
      @start-learning="handleStartLearning"
    />
    
    <!-- Main content area -->
    <main class="main-content">
      <router-view />
    </main>

    <!-- Global Footer -->
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { db, auth } from './firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import UserMenu from './components/UserMenu.vue';
import ProfileCompletion from './components/ProfileCompletion.vue';
import QuestionSourceSplash from './components/QuestionSourceSplash.vue';
import AppFooter from './components/AppFooter.vue';

// Router instance
const router = useRouter();

// User state
const user = ref(null);
const userProfile = ref({});
const isPremium = ref(false);
const subscriptionDate = ref(null);
const nextBillingDate = ref(null);
const showProfileCompletion = ref(false);
const showQuestionSourceSplash = ref(false);
const mobileMenuOpen = ref(false);
const userMenuOpen = ref(false);

// Admin check - only show admin tools for specific email
const isAdmin = computed(() => {
  return user.value?.email === 'joeperrotta1979@gmail.com';
});

// Auth state listener
let authUnsubscribe = null;

onMounted(() => {
  authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
    user.value = firebaseUser;
    
    if (firebaseUser) {
      await loadUserProfile();
      await loadPremiumStatus();
      
      // Check if user is K-2 grade and redirect to kid dashboard
      console.log('🔍 Checking user profile:', userProfile.value);
      console.log('🔍 Grade level:', userProfile.value.gradeLevel);
      console.log('🔍 Current path:', window.location.pathname);
      
      // Only Kindergarten (grade 'K') should use kid dashboard
      if (userProfile.value.gradeLevel === 'K') {
        const currentPath = router.currentRoute.value.path;
        
        // Allowed routes for kindergarten users (can access these without redirect)
        const allowedKidRoutes = [
          '/kid-dashboard',
          '/kid-quiz',
          '/achievements',  // Allow achievements
          '/subscription',  // Allow subscription
          '/profile'        // Allow profile
        ];
        
        // Check if current path is an allowed route or starts with kid- prefix
        const isAllowedRoute = allowedKidRoutes.some(route => currentPath.startsWith(route)) || 
                               currentPath.startsWith('/kid-');
        
        // If trying to access /dashboard, redirect to /kid-dashboard
        if (currentPath === '/dashboard') {
          console.log('🎓 Kindergarten user accessing /dashboard - redirecting to /kid-dashboard');
          router.push('/kid-dashboard');
        }
        // Only redirect if not already on an allowed route
        else if (!isAllowedRoute) {
          console.log('🎓 Redirecting Kindergarten user to kid dashboard');
          router.push('/kid-dashboard');
        } else {
          console.log('🎓 Kindergarten user on allowed route, no redirect needed');
        }
      } else {
        console.log('🎓 Not a Kindergarten user, staying on regular dashboard');
      }
    } else {
      userProfile.value = {};
      isPremium.value = false;
      subscriptionDate.value = null;
      nextBillingDate.value = null;
    }
  });
  
  // Listen for manual splash page trigger (for testing/admin)
  window.addEventListener('show-question-source-splash', () => {
    if (user.value && userProfile.value) {
      showQuestionSourceSplash.value = true;
    }
  });
  
  // Listen for profile updates from UserMenu component
  window.addEventListener('profile-updated', async () => {
    if (user.value) {
      await loadUserProfile();
      await loadPremiumStatus();
    }
  });
  
  // Listen for force admin premium event from TempAdmin
  window.addEventListener('force-admin-premium', (event) => {
    if (event.detail.email === 'joeperrotta1979@gmail.com') {
      isPremium.value = true;
      userProfile.value = {
        firstName: event.detail.firstName,
        lastName: event.detail.lastName,
        gradeLevel: event.detail.gradeLevel,
        state: event.detail.state,
        email: event.detail.email
      };
    }
  });
});

onUnmounted(() => {
  if (authUnsubscribe) {
    authUnsubscribe();
  }
});

async function loadUserProfile() {
  if (!user.value) {
    console.log('🔍 No user, skipping profile load');
    return;
  }
  
  console.log('🔍 Loading user profile for:', user.value.uid);
  console.log('🔍 Firebase project:', db.app.options.projectId);
  console.log('🔍 Database path: users/', user.value.uid);
  
  // Retry mechanism for newly created accounts
  let retries = 0;
  const maxRetries = 5;
  
  while (retries < maxRetries) {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.value.uid));
      console.log('🔍 User document exists:', userDoc.exists());
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('🔍 User data from Firebase:', userData);
        userProfile.value = userData;
        
        // Check if we should show the question source splash
        // Show if user hasn't seen it yet and has completed profile
        if (!userProfile.value.hasSeenQuestionSourceSplash && userProfile.value.state && userProfile.value.firstName) {
          setTimeout(() => {
            showQuestionSourceSplash.value = true;
          }, 1500); // Short delay to let the UI settle
        }
        return; // Success, exit the retry loop
      } else {
        console.log('🔍 User document does not exist in Firebase, retry', retries + 1);
        retries++;
        if (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
        }
      }
    } catch (error) {
      console.error('🔍 Error loading user profile:', error);
      retries++;
      if (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
      }
    }
  }
  
  // If we get here, all retries failed
  console.log('🔍 User document still does not exist after', maxRetries, 'retries');
  userProfile.value = {};
}

async function loadPremiumStatus() {
  if (!user.value) return;
  
  // Grant premium status to admin users first (reliable detection)
  if (user.value?.email === 'joeperrotta1979@gmail.com') {
    isPremium.value = true;
    return; // Skip Firestore check for admin user
  }
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.value.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      isPremium.value = data.isPremium || false;
      subscriptionDate.value = data.subscriptionDate || null;
      nextBillingDate.value = data.nextBillingDate || null;
    }
  } catch (error) {
    console.error('Error loading premium status:', error);
    // Fallback: If Firestore fails and this is admin email, grant premium
    if (user.value?.email === 'joeperrotta1979@gmail.com') {
      isPremium.value = true;
    }
  }
}

async function handleProfileCompleted() {
  showProfileCompletion.value = false;
  await loadUserProfile();
  
  // After profile completion, show question source splash if appropriate
  if (userProfile.value.state && userProfile.value.firstName && !userProfile.value.hasSeenQuestionSourceSplash) {
    setTimeout(() => {
      showQuestionSourceSplash.value = true;
    }, 800); // Brief delay after profile completion
  }
}

async function handleLogout() {
  try {
    await signOut(auth);
    // Redirect to login page after successful logout
    router.push('/');
  } catch (error) {
    console.error('Logout error:', error);
  }
}

async function handleSplashClose() {
  showQuestionSourceSplash.value = false;
  await markSplashAsSeen();
}

async function handleStartLearning() {
  showQuestionSourceSplash.value = false;
  await markSplashAsSeen();
  // Navigate to quiz or dashboard
  if (window.location.pathname === '/' || window.location.pathname === '/dashboard') {
    // If on main page, scroll to or show quiz section
    const quizSection = document.querySelector('[data-quiz-section]');
    if (quizSection) {
      quizSection.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

async function markSplashAsSeen() {
  if (!user.value?.uid) return;
  
  try {
    const userDocRef = doc(db, 'users', user.value.uid);
    await updateDoc(userDocRef, {
      hasSeenQuestionSourceSplash: true,
      questionSourceSplashSeenAt: new Date()
    });
    
    // Update local profile
    userProfile.value = {
      ...userProfile.value,
      hasSeenQuestionSourceSplash: true
    };
    
  } catch (error) {
    console.error('Error marking splash as seen:', error);
  }
}

// Mobile menu functions
function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

// User menu functions
function handleUserMenuOpen() {
  userMenuOpen.value = true;
  // Close mobile menu when user menu opens
  closeMobileMenu();
}

function handleUserMenuClose() {
  userMenuOpen.value = false;
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&family=Caveat:wght@400;600;700&display=swap');
@import './assets/responsive-fixes.css';

.app-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  display: flex;
  flex-direction: column;
}

/* User Profile Top Banner */
.user-profile-top-banner {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 1001;
}

.user-profile-content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.user-profile-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0;
  padding: 0.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.user-menu-top {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.premium-badge-inline {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #1a1a1a;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
  animation: premiumBadgeGlow 2s ease-in-out infinite;
}

@keyframes premiumBadgeGlow {
  0%, 100% { 
    box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3); 
    transform: scale(1);
  }
  50% { 
    box-shadow: 0 4px 16px rgba(255, 215, 0, 0.5); 
    transform: scale(1.02);
  }
}

/* Chalkboard Header Styles */
.chalkboard-header {
  padding: 2rem 1rem;
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.chalkboard-frame {
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(145deg, #8B4513 0%, #A0522D 50%, #8B4513 100%);
  border: 8px solid #654321;
  border-radius: 12px;
  padding: 8px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.1),
    inset 0 -2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
}

.chalkboard-frame::before {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  background: linear-gradient(145deg, #654321 0%, #8B4513 50%, #654321 100%);
  border-radius: 16px;
  z-index: -1;
  box-shadow: 
    0 4px 16px rgba(0, 0, 0, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.1);
}

.chalkboard-surface {
  background: 
    radial-gradient(ellipse at 20% 30%, rgba(200, 200, 200, 0.04) 0%, transparent 40%),
    radial-gradient(ellipse at 80% 70%, rgba(180, 180, 180, 0.03) 0%, transparent 45%),
    radial-gradient(ellipse at 40% 90%, rgba(160, 160, 160, 0.025) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 10%, rgba(220, 220, 220, 0.02) 0%, transparent 35%),
    radial-gradient(ellipse at 90% 40%, rgba(140, 140, 140, 0.015) 0%, transparent 60%),
    linear-gradient(145deg, #1a2b1a 0%, #152015 20%, #0f1b0f 40%, #0a150a 60%, #152015 80%, #1a2b1a 100%);
  border-radius: 8px;
  padding: 3rem 2rem;
  position: relative;
  overflow: hidden;
  box-shadow: 
    inset 0 0 120px rgba(0, 0, 0, 0.4),
    inset 0 0 60px rgba(0, 0, 0, 0.3),
    inset 10px 10px 30px rgba(0, 0, 0, 0.2),
    inset -10px -10px 30px rgba(0, 0, 0, 0.2);
}

.chalkboard-surface::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(circle at 15% 25%, rgba(255, 255, 255, 0.012) 1px, transparent 1px),
    radial-gradient(circle at 35% 65%, rgba(255, 255, 255, 0.008) 1px, transparent 1px),
    radial-gradient(circle at 65% 35%, rgba(255, 255, 255, 0.01) 1px, transparent 1px),
    radial-gradient(circle at 85% 75%, rgba(255, 255, 255, 0.007) 1px, transparent 1px),
    radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.009) 0.5px, transparent 0.5px),
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 4px,
      rgba(255, 255, 255, 0.01) 4px,
      rgba(255, 255, 255, 0.01) 8px
    ),
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 6px,
      rgba(255, 255, 255, 0.008) 6px,
      rgba(255, 255, 255, 0.008) 12px
    );
  background-size: 40px 40px, 25px 25px, 60px 60px, 30px 30px, 15px 15px, 100% 100%, 100% 100%;
  pointer-events: none;
  opacity: 0.6;
}

.chalkboard-surface::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(ellipse 200px 100px at 10% 20%, rgba(245, 245, 240, 0.008) 0%, transparent 70%),
    radial-gradient(ellipse 150px 80px at 90% 30%, rgba(245, 245, 240, 0.006) 0%, transparent 70%),
    radial-gradient(ellipse 180px 120px at 20% 80%, rgba(245, 245, 240, 0.007) 0%, transparent 70%),
    radial-gradient(ellipse 100px 60px at 80% 90%, rgba(245, 245, 240, 0.005) 0%, transparent 70%);
  pointer-events: none;
  opacity: 0.3;
}

.chalkboard-content {
  position: relative;
  z-index: 1;
}

/* Chalk Animation Styles */
@keyframes drawChalkLetter {
  0% {
    opacity: 0;
    transform: scale(0.5) rotate(-5deg) translateY(20px);
    filter: blur(2px);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1) rotate(2deg) translateY(-5px);
    filter: blur(1px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg) translateY(0px);
    filter: blur(0px);
  }
}

@keyframes chalkDust {
  0% {
    opacity: 0;
    transform: translateY(0px);
  }
  30% {
    opacity: 0.6;
    transform: translateY(-3px);
  }
  100% {
    opacity: 0;
    transform: translateY(-8px);
  }
}

@keyframes fadeInChalk {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chalkboard-title {
  font-size: 4rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  position: relative;
  height: 5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chalk-text {
  font-family: 'Kalam', 'Caveat', cursive, monospace;
  color: #f5f5f0;
  position: relative;
  display: inline-block;
}

.chalk-letter {
  display: inline-block;
  position: relative;
  color: #f5f5f0;
  text-shadow: 
    0 0 5px rgba(245, 245, 240, 0.8),
    0 0 10px rgba(245, 245, 240, 0.5),
    0 0 15px rgba(245, 245, 240, 0.3),
    1px 1px 2px rgba(0, 0, 0, 0.9),
    2px 2px 4px rgba(0, 0, 0, 0.7),
    3px 3px 6px rgba(0, 0, 0, 0.5);
  filter: 
    drop-shadow(0 0 4px rgba(245, 245, 240, 0.6))
    contrast(1.3);
  opacity: 0;
  animation: drawChalkLetter 0.8s ease-out forwards;
}

.chalk-letter::before {
  content: '';
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  background: 
    radial-gradient(circle at 25% 35%, rgba(245, 245, 240, 0.15) 2px, transparent 2px),
    radial-gradient(circle at 65% 75%, rgba(245, 245, 240, 0.1) 1px, transparent 1px),
    radial-gradient(circle at 85% 25%, rgba(245, 245, 240, 0.12) 1px, transparent 1px),
    radial-gradient(circle at 15% 80%, rgba(245, 245, 240, 0.08) 1px, transparent 1px);
  background-size: 25px 25px, 15px 15px, 35px 35px, 20px 20px;
  z-index: -1;
  border-radius: 3px;
  animation: chalkDust 0.8s ease-out;
  animation-delay: inherit;
}

.chalk-letter::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    transparent 20%,
    rgba(245, 245, 240, 0.1) 25%,
    rgba(245, 245, 240, 0.2) 30%,
    transparent 35%,
    transparent 65%,
    rgba(245, 245, 240, 0.15) 70%,
    transparent 75%
  );
  background-size: 300% 300%;
  animation: shimmer 4s ease-in-out infinite;
  animation-delay: calc(var(--delay, 0s) + 1s);
  border-radius: 2px;
}

.chalk-space {
  display: inline-block;
  width: 0.5em;
}

@keyframes shimmer {
  0%, 100% {
    background-position: -300% -300%;
    opacity: 0;
  }
  50% {
    background-position: 300% 300%;
    opacity: 0.8;
  }
}

.chalkboard-subtitle {
  font-family: 'Kalam', 'Caveat', cursive, monospace;
  font-size: 1.5rem;
  color: #e8e8e0;
  margin-bottom: 1.5rem;
  letter-spacing: 1px;
  position: relative;
  animation: fadeInChalk 1.5s ease-out 3s both;
}

.chalk-subtitle {
  text-shadow: 
    0 0 3px rgba(232, 232, 224, 0.4),
    0 0 6px rgba(232, 232, 224, 0.3),
    1px 1px 2px rgba(0, 0, 0, 0.7);
  filter: contrast(1.1);
}

.chalkboard-welcome {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-right: 2rem;
}

/* Navigation Menu Styles */
.navigation-menu {
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  padding: 0.5rem 1rem;
}

/* Mobile Menu Toggle Button */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  position: relative;
  width: 44px;
  height: 40px;
  margin-left: auto;
  transition: all 0.3s ease;
  justify-content: center;
  align-items: center;
}

.mobile-menu-toggle:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.hamburger-line {
  width: 28px;
  height: 4px;
  background-color: #fff;
  margin: 3px 0;
  transition: 0.3s;
  transform-origin: center;
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.hamburger-line.active:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger-line.active:nth-child(2) {
  opacity: 0;
}

.hamburger-line.active:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Desktop Navigation Container */
.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem 1.5rem;
  padding: 0.75rem 1rem;
}

/* Mobile Menu Overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: none;
}

.nav-item {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #f0f0f0;
  font-size: 1.1rem;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: background-color 0.3s ease, color 0.3s ease;
  white-space: nowrap;
  flex: 0 1 190px;
  text-align: center;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.nav-item.active {
  background-color: #667eea;
  color: #fff;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.nav-icon {
  font-size: 1.5rem;
  margin-right: 0.5rem;
}

.nav-text {
  font-family: 'Courier New', 'Monaco', 'Menlo', monospace;
  letter-spacing: 1px;
  text-transform: uppercase;
}

/* Admin Section */
.admin-section {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.admin-title {
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.admin-item {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.admin-item:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Main Content */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  margin-top: 1rem;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(5px);
}

.modal {
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.modal-loading {
  padding: 2rem;
  text-align: center;
  color: #666;
}

/* Responsive Design */
/* Tablet styles */
@media (max-width: 1024px) and (min-width: 769px) {
  .chalkboard-title {
    font-size: 3.5rem;
  }
  
  .chalkboard-subtitle {
    font-size: 1.4rem;
  }
  
  .nav-container {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }

  .nav-item {
    min-width: 150px;
    text-align: center;
    flex: 0 1 160px;
  }
  
  .main-content {
    padding: 1.5rem;
  }
}

/* Mobile styles */
@media (max-width: 768px), (max-height: 500px) and (orientation: landscape) {
  .user-profile-top-banner {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
  }
  
  .user-profile-content {
    width: 100%;
  }
  
  .user-profile-text {
    font-size: 0.95rem;
    padding: 0.4rem 1rem;
  }
  
  .user-menu-top {
    width: 100%;
    justify-content: center;
  }
  .chalkboard-title {
    font-size: 2.5rem;
    height: 3.5rem;
  }
  
  .chalkboard-subtitle {
    font-size: 1.2rem;
  }
  
  .chalkboard-header {
    padding: 1rem 0.5rem;
  }
  
  .chalkboard-surface {
    padding: 1.5rem 1rem;
  }
  
  .main-content {
    padding: 1rem 0.5rem;
  }

  /* Mobile Menu Toggle */
  .mobile-menu-toggle {
    display: flex;
  }

  /* Hide menu by default on mobile */
  .nav-container {
    position: fixed;
    top: 60px;
    left: -100%;
    width: 280px;
    height: calc(100vh - 60px);
    background: rgba(0, 0, 0, 0.95);
    backdrop-filter: blur(15px);
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem 0;
    gap: 0;
    transition: left 0.3s ease-in-out;
    overflow-y: auto;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
  }

  /* Show menu when mobile-open class is added */
  .nav-container.mobile-open {
    left: 0;
  }

  /* Mobile menu overlay */
  .mobile-menu-overlay {
    display: block;
  }

  .nav-item {
    width: 100%;
    justify-content: flex-start;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    margin: 0;
    flex: none;
  }

  .nav-item:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  .nav-icon {
    font-size: 1.25rem;
    margin-right: 1rem;
    min-width: 30px;
  }
  
  .nav-text {
    font-size: 0.9rem;
  }
  
  .admin-section {
    margin-top: 1rem;
    width: 100%;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .admin-title {
    padding: 0 1.5rem;
    margin-bottom: 1rem;
  }

  .admin-item {
    background-color: rgba(255, 255, 255, 0.02);
    border-left: 3px solid #667eea;
  }
  
  .modal {
    width: 95%;
    max-width: none;
    margin: 0 auto;
  }
}

/* Small mobile styles */
@media (max-width: 480px) {
  .user-profile-top-banner {
    padding: 0.5rem 0.75rem;
  }
  
  .user-profile-text {
    font-size: 0.85rem;
    padding: 0.35rem 0.75rem;
  }
  
  .chalkboard-title {
    font-size: 2rem;
    height: 3rem;
  }
  
  .chalkboard-subtitle {
    font-size: 1rem;
  }
  
  .chalkboard-header {
    padding: 0.75rem 0.25rem;
  }
  
  .chalkboard-surface {
    padding: 1rem 0.5rem;
  }

  /* Smaller mobile menu */
  .nav-container {
    width: 260px;
  }
  
  .nav-item {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }

  .nav-icon {
    font-size: 1.1rem;
    margin-right: 0.75rem;
    min-width: 25px;
  }
  
  .nav-text {
    font-size: 0.8rem;
  }
}

/* Landscape orientation specific fixes */
@media (orientation: landscape) and (max-height: 500px) {
  .chalkboard-header {
    padding: 0.5rem;
  }

  .chalkboard-title {
    font-size: 2rem;
    height: 2.5rem;
  }

  .chalkboard-subtitle {
    font-size: 1rem;
  }

  .main-content {
    padding: 0.5rem;
  }

  /* Force mobile menu on landscape even on larger screens */
  .mobile-menu-toggle {
    display: flex !important;
  }

  .nav-container:not(.mobile-open) {
    left: -100% !important;
  }

  .nav-container {
    width: 250px;
    height: calc(100vh - 50px);
    top: 50px;
  }

  .nav-item {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    flex: none;
  }

  .nav-icon {
    font-size: 1.1rem;
    margin-right: 0.75rem;
  }

  .nav-text {
    font-size: 0.8rem;
  }
}
</style> 